import React from 'react';
import Select, {components} from "react-select";
import {X} from "lucide-react";
import UseContext from "hooks/useContext";
import {SelectDropdownIndicator, selectStyles} from "components/componentsForm/selectStyles";

// An option of the menu with its checkbox, ticked when the option is picked.
const CheckboxOption = (props) => (
    <components.Option {...props}>
        <span className="flex items-center gap-3">
            <input
                type="checkbox"
                checked={props.isSelected}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="checkbox-primary pointer-events-none"
            />
            {props.label}
        </span>
    </components.Option>
);

/**
 * Multiple choice field (tools, licences, languages): the shared select box (selectStyles) to pick,
 * and the picked values below it as wrapping badges, each with its cross to remove it. The box
 * itself keeps only its placeholder.
 *
 * `groupOf(value)`: at most one value per group, the last picked replaces the other one (a
 * language at one level only: "Anglais - Courant" replaces "Anglais - Débutant").
 * `compare(a, b)`: order of the options and of the picked badges, by value.
 * `showValues={false}`: no badges, the caller lists the picked values itself (job template tools).
 *
 * The menu keeps the picked options, with a checked box in front of each: a click unticks it.
 */
const MultiDdl = ({data, selectArray, defaultSelect, placeholder, noOptionsMessage, groupOf, compare, showValues = true}) => {
    const {language} = UseContext();

    const options = data.map((d) => ({value: d.name, label: d.name}));
    if (compare) options.sort((a, b) => compare(a.value, b.value));
    const selected = defaultSelect ? options.filter((opt) => defaultSelect.includes(opt.value)) : [];
    const removeLabel = language.getValue('select.remove', 'Retirer');

    const handleChange = (values) => {
        let picked = (values || []).map((option) => option.value);
        if (groupOf) {
            // react-select appends the new value last: keep the last value of each group.
            const lastOfGroup = new Map(picked.map((value) => [groupOf(value), value]));
            picked = picked.filter((value) => lastOfGroup.get(groupOf(value)) === value);
        }
        selectArray(picked);
    };
    const remove = (value) => selectArray(selected.map((option) => option.value).filter((v) => v !== value));

    return (
        <div className="flex flex-col gap-2">
            <Select
                isMulti
                closeMenuOnSelect={false}
                controlShouldRenderValue={false}
                isClearable={false}
                hideSelectedOptions={false}
                onChange={handleChange}
                value={selected}
                options={options}
                styles={selectStyles}
                classNamePrefix="react-select"
                placeholder={placeholder || language.getValue('select.placeholder', 'Sélectionner…')}
                noOptionsMessage={() => noOptionsMessage || language.getValue('select.noOptions', 'Aucun résultat')}
                components={{DropdownIndicator: SelectDropdownIndicator, Option: CheckboxOption}}
            />
            {showValues && selected.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {selected.map((option) => (
                        <span key={option.value} className="badge bg-gray-100 text-gray-700 pr-2">
                            {option.label}
                            <button
                                type="button"
                                onClick={() => remove(option.value)}
                                aria-label={`${removeLabel} ${option.label}`}
                                className="rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                            >
                                <X size={14} aria-hidden="true"/>
                            </button>
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default MultiDdl;
